import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { loadBase, loadDefensiveStats, loadAdvancedStats } from '../../services/espnService';
import { useConfig } from '../../hooks/useConfig';

const DEF_FORMULAS = {
  DE: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles', '=prss', '=hrry'],
  DT: ['sacks', 'QBHits', 'tacklesForLoss', 'totalTackles', 'forcedFumbles', '=prss', '=hrry'],
  LB: ['totalTackles', 'sacks', 'tacklesForLoss', 'passesDefended', 'forcedFumbles', '=prss', '=~m_tkl_percent'],
  CB: ['passesDefended', 'interceptions', 'totalTackles', '=~yds_tgt', '=~m_tkl_percent'],
  S:  ['totalTackles', 'passesDefended', 'interceptions', '=~yds_tgt', '=~m_tkl_percent'],
};

const STAT_LABELS = {
  sacks:           'Sacks/G',
  QBHits:          'QB Hits/G',
  tacklesForLoss:  'TFL/G',
  totalTackles:    'Tackles/G',
  passesDefended:  'PD/G',
  interceptions:   'INT/G',
  forcedFumbles:   'FF/G',
  // Advanced stats
  '=prss':          'Pressures',
  '=hrry':          'Hurries',
  '=~m_tkl_percent':'MTkl% ↓',
  '=~yds_tgt':      'Yds/Tgt ↓',
};

// Key format: ["="] raw stat ["~"] penalty
function parseKey(key) {
  let k = key;
  const isRaw     = k.startsWith('='); if (isRaw)     k = k.slice(1);
  const isPenalty = k.startsWith('~'); if (isPenalty) k = k.slice(1);
  return { rawKey: k, isRaw, isPenalty };
}

const DEF_POS_CAPS = { DE: 65, DT: 65, LB: 129, CB: 65, S: 65 };

const MIN_GAMES = 4;

const POSITIONS = ['DE', 'DT', 'LB', 'CB', 'S'];

function computeNR(pos, players, defStats, weights = {}) {
  const statKeys = DEF_FORMULAS[pos];
  const cap = DEF_POS_CAPS[pos] ?? 65;

  // Filter eligible players
  const eligible = players.filter(p => {
    const s = defStats[p.id];
    return s && s.gamesPlayed >= MIN_GAMES;
  }).map(p => {
    const s = defStats[p.id];
    const gp = s.gamesPlayed;
    const perGame = {};
    statKeys.forEach(key => {
      const { rawKey, isRaw } = parseKey(key);
      const val = s[rawKey] ?? 0;
      perGame[key] = isRaw ? val : (gp > 0 ? val / gp : 0);
    });
    return { ...p, perGame, gamesPlayed: gp };
  });

  if (!eligible.length) return [];

  // Rank each stat independently (skip-rank ties); penalty/lower-is-better sorts ascending
  const statRanks = {};
  eligible.forEach(p => { statRanks[p.id] = {}; });

  statKeys.forEach(statKey => {
    const { isPenalty } = parseKey(statKey);
    const sorted = [...eligible].sort((a, b) =>
      isPenalty ? a.perGame[statKey] - b.perGame[statKey] : b.perGame[statKey] - a.perGame[statKey]
    );
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[statKey];
      let j = i;
      while (j < sorted.length && sorted[j].perGame[statKey] === tieVal) {
        statRanks[sorted[j].id][statKey] = rank;
        j++;
      }
      rank += j - i;
      i = j;
    }
  });

  // Sum weighted ranks, sort ascending
  const withNR = eligible.map(p => {
    const rankSum = statKeys.reduce((acc, k) => {
      const w = weights[k] ?? 1;
      return acc + (statRanks[p.id][k] ?? eligible.length) * w;
    }, 0);
    return { ...p, rankSum };
  }).sort((a, b) => a.rankSum - b.rankSum);

  // Assign NR with skip-rank ties
  const result = [];
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum;
    let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) {
      result.push({ ...withNR[j], nr });
      j++;
    }
    nr += j - i;
    i = j;
  }

  // Non-qualifiers at cap
  const eligibleIds = new Set(result.map(p => p.id));
  players.forEach(p => {
    if (!eligibleIds.has(p.id)) {
      result.push({ ...p, nr: cap, perGame: {}, gamesPlayed: defStats[p.id]?.gamesPlayed ?? 0 });
    }
  });

  return result;
}

const cellSx = {
  color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.75, px: 1,
};
const headerCellSx = {
  color: '#f5a623', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)',
  py: 0.75, px: 1, backgroundColor: '#060e1a',
};

export default function DefensiveNR() {
  const { config } = useConfig();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [players, setPlayers] = useState({});
  const [defStats, setDefStats] = useState({});
  const [posTab, setPosTab] = useState(0);

  useEffect(() => {
    setLoading(true);
    loadBase()
      .then(({ players: p }) => {
        const defPlayers = {};
        Object.values(p).forEach(pl => {
          if (['DE', 'DT', 'LB', 'CB', 'S'].includes(pl.posKey)) {
            defPlayers[pl.id] = pl;
          }
        });
        setPlayers(defPlayers);
        return Promise.all([
          loadDefensiveStats(Object.keys(defPlayers)),
          loadAdvancedStats(),
        ]);
      })
      .then(([stats, adv]) => {
        // Merge advanced stats into the defensive stats map (additive)
        const merged = { ...stats };
        Object.keys(merged).forEach(id => {
          if (adv[id]) merged[id] = { ...merged[id], ...adv[id] };
        });
        setDefStats(merged);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load defensive player data.'); setLoading(false); });
  }, []);

  const currentPos = POSITIONS[posTab];
  const statKeys = DEF_FORMULAS[currentPos] || [];

  const ranked = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(defStats).length) return [];
    const posPlayers = Object.values(players).filter(p => p.posKey === currentPos);
    const weights = config[`nr_weights_def_${currentPos.toLowerCase()}`] ?? {};
    return computeNR(currentPos, posPlayers, defStats, weights);
  }, [players, defStats, currentPos, config]);

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#2ea84b' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Loading defensive stats…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  const qualifiers = ranked.filter(p => p.perGame && Object.keys(p.perGame).length > 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Defensive Nerd Rating
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2, lineHeight: 1.6 }}>
        Equal-weight rank-sum across key stats per game. Min {MIN_GAMES} games played to qualify.
      </Typography>

      <Tabs
        value={posTab}
        onChange={(_, v) => setPosTab(v)}
        textColor="inherit"
        indicatorColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          mb: 2,
          borderBottom: '1px solid rgba(245,166,35,0.15)',
          '& .MuiTab-root': {
            minHeight: 36, fontWeight: 700, textTransform: 'none',
            fontSize: '0.82rem', px: 2, color: '#9eb8d0',
          },
          '& .Mui-selected': { color: '#f5a623' },
        }}
      >
        {POSITIONS.map(pos => (
          <Tab key={pos} label={pos} />
        ))}
      </Tabs>

      {qualifiers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#6a8aa8' }}>No qualifying {currentPos}s found (min {MIN_GAMES} games).</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellSx, width: 48 }}>NR</TableCell>
                <TableCell sx={{ ...headerCellSx, width: 56 }}>Team</TableCell>
                <TableCell align="center" sx={{ ...headerCellSx, width: 36 }}>GP</TableCell>
                <TableCell sx={{ ...headerCellSx, minWidth: 140 }}>Name</TableCell>
                {statKeys.map(k => (
                  <TableCell key={k} align="right" sx={headerCellSx}>{STAT_LABELS[k] ?? k}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {qualifiers.map((p, idx) => (
                <TableRow key={p.id} sx={{
                  backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                  '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
                }}>
                  <TableCell sx={{ ...cellSx, fontWeight: 900, color: p.nr <= 5 ? '#f5a623' : '#e8f0f8', fontSize: '0.82rem', width: 48 }}>
                    #{p.nr}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, width: 56 }}>
                    <Chip label={p.team} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: 'rgba(245,166,35,0.1)', color: '#f5a623', border: '1px solid rgba(245,166,35,0.25)' }} />
                  </TableCell>
                  <TableCell align="center" sx={{ ...cellSx, width: 36 }}>{p.gamesPlayed}</TableCell>
                  <TableCell sx={{ ...cellSx, color: '#e8f0f8', fontWeight: 600 }}>{p.name}</TableCell>
                  {statKeys.map(k => (
                    <TableCell key={k} align="right" sx={cellSx}>
                      {p.perGame[k] != null ? p.perGame[k].toFixed(2) : '—'}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
